 
import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select } from '../components/ui/select';
import { Download, FileText, TrendingUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FinanceReportsPage() {
  const [reportType, setReportType] = useState('income-summary');
  const [term, setTerm] = useState('1');
  const [academicYear, setAcademicYear] = useState('2024');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const reportTypes = [
    { value: 'income-summary', label: 'Income Summary by Category' },
    { value: 'expense-summary', label: 'Expense Summary by Category' },
    { value: 'balance-sheet', label: 'Financial Balance Sheet' },
    { value: 'student-fees', label: 'Student Fees Breakdown' },
    { value: 'trip-finance', label: 'Trip Finance Report' },
    { value: 'grocery-cash', label: 'Grocery Cash Summary' },
    { value: 'uniform-status', label: 'Uniform Collection Status' },
    { value: 'salary-summary', label: 'Teacher Salary Summary' },
    { value: 'statutory-charges', label: 'Statutory Charges Report' },
    { value: 'audit-log', label: 'Finance Audit Log' },
  ];

  const generateReport = async () => {
    try {
      setLoading(true);
      
      // Fetch report data
      const response = await fetch(`${API}/reports/${reportType}?term=${term}&academicYear=${academicYear}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = async () => {
    try {
      const response = await fetch(`${API}/reports/${reportType}/export?format=pdf&term=${term}&academicYear=${academicYear}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportType}-${term}-${academicYear}.pdf`;
        a.click();
      }
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Failed to export PDF');
    }
  };

  const exportToExcel = async () => {
    try {
      const response = await fetch(`${API}/reports/${reportType}/export?format=excel&term=${term}&academicYear=${academicYear}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${reportType}-${term}-${academicYear}.xlsx`;
        a.click();
      }
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Failed to export Excel');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Finance Reports</h1>
        <p className="text-gray-600 mt-1">Generate and export financial reports for analysis and audit</p>
      </div>

      {/* Report Selection */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Configuration</h2>
        
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Type
              </label>
              <Select
                value={reportType}
                onChange={(e) => {
                  setReportType(e.target.value);
                  setReportData(null);
                }}
              >
                {reportTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Term
                </label>
                <Select value={term} onChange={(e) => setTerm(e.target.value)}>
                  <option value="1">Term 1</option>
                  <option value="2">Term 2</option>
                  <option value="3">Term 3</option>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Academic Year
                </label>
                <Select value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                  <option value="2026">2026</option>
                </Select>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={generateReport}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>

            {reportData && (
              <>
                <Button
                  onClick={exportToPDF}
                  className="bg-red-600 hover:bg-red-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
                <Button
                  onClick={exportToExcel}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Excel
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      {/* Report Display */}
      {reportData ? (
        <div className="space-y-4">
          {/* Report Header */}
          <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <div className="flex items-center gap-4">
              <FileText className="w-8 h-8 text-blue-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {reportTypes.find(t => t.value === reportType)?.label}
                </h3>
                <p className="text-sm text-gray-600">
                  Term {term} • Academic Year {academicYear} • Generated {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>
          </Card>

          {/* Income Summary Report */}
          {reportType === 'income-summary' && reportData.items && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-6 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                  <p className="text-sm text-gray-600">Total Income</p>
                  <p className="text-3xl font-bold text-green-700">${reportData.totalIncome?.toFixed(2) || '0.00'}</p>
                </Card>
                <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                  <p className="text-sm text-gray-600">Categories</p>
                  <p className="text-3xl font-bold text-blue-700">{reportData.items?.length || 0}</p>
                </Card>
                <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                  <p className="text-sm text-gray-600">Expected</p>
                  <p className="text-3xl font-bold text-purple-700">${reportData.expectedIncome?.toFixed(2) || '0.00'}</p>
                </Card>
              </div>

              <Card className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100 border-b-2 border-gray-300">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Category</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Percentage</th>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.items?.map((item, idx) => (
                      <tr key={idx} className="border-b hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.category}</td>
                        <td className="px-6 py-4 text-sm text-green-700 font-semibold">${item.amount?.toFixed(2) || '0.00'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className="bg-blue-600 h-full"
                                style={{width: `${item.percentage || 0}%`}}
                              />
                            </div>
                            {item.percentage?.toFixed(1)}%
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{item.count || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
          )}

          {/* Balance Sheet Report */}
          {reportType === 'balance-sheet' && reportData && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="p-6 bg-green-50 border-green-200">
                <h3 className="text-lg font-semibold text-green-900 mb-4">Assets</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Cash in Hand</span>
                    <span className="font-semibold">${reportData.assets?.cash?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-700 font-semibold">Total Assets</span>
                    <span className="font-bold text-green-700">${reportData.totalAssets?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-red-50 border-red-200">
                <h3 className="text-lg font-semibold text-red-900 mb-4">Liabilities</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Outstanding Payments</span>
                    <span className="font-semibold">${reportData.liabilities?.outstanding?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-700 font-semibold">Total Liabilities</span>
                    <span className="font-bold text-red-700">${reportData.totalLiabilities?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
              </Card>

              <Card className="p-6 bg-blue-50 border-blue-200 md:col-span-2">
                <h3 className="text-lg font-semibold text-blue-900 mb-4">Net Position</h3>
                <div className="flex justify-between items-center">
                  <span className="text-lg text-gray-700">Total Assets - Total Liabilities</span>
                  <span className={`text-3xl font-bold ${(reportData.totalAssets || 0) - (reportData.totalLiabilities || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                    ${((reportData.totalAssets || 0) - (reportData.totalLiabilities || 0)).toFixed(2)}
                  </span>
                </div>
              </Card>
            </div>
          )}

          {/* Generic Table Display for Other Reports */}
          {reportData.items && !['income-summary', 'balance-sheet'].includes(reportType) && (
            <Card className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100 border-b-2 border-gray-300">
                  <tr>
                    {Object.keys(reportData.items[0] || {}).map(key => (
                      <th key={key} className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                        {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reportData.items.map((item, idx) => (
                    <tr key={idx} className="border-b hover:bg-gray-50">
                      {Object.values(item).map((value, valIdx) => (
                        <td key={valIdx} className="px-6 py-4 text-sm text-gray-600">
                          {typeof value === 'number' ? `$${value.toFixed(2)}` : value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          {/* No Data Message */}
          {!reportData.items && (
            <Card className="p-8 text-center bg-gray-50">
              <p className="text-gray-500">No data available for this report</p>
            </Card>
          )}

          {/* Export Options */}
          <div className="flex gap-2 justify-center">
            <Button onClick={exportToPDF} className="bg-red-600 hover:bg-red-700">
              <Download className="w-4 h-4 mr-2" /> Export as PDF
            </Button>
            <Button onClick={exportToExcel} className="bg-green-600 hover:bg-green-700">
              <Download className="w-4 h-4 mr-2" /> Export as Excel
            </Button>
          </div>
        </div>
      ) : (
        <Card className="p-8 text-center bg-gray-50">
          <p className="text-gray-500">Select report parameters and click "Generate Report" to view data</p>
        </Card>
      )}

      {/* Common Reports Quick Access */}
      <Card className="p-6 bg-gray-50">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Reports</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Button onClick={() => { setReportType('income-summary'); setReportData(null); }} className="bg-blue-600 hover:bg-blue-700 justify-start">
            <TrendingUp className="w-4 h-4 mr-2" /> Income Summary
          </Button>
          <Button onClick={() => { setReportType('balance-sheet'); setReportData(null); }} className="bg-purple-600 hover:bg-purple-700 justify-start">
            <FileText className="w-4 h-4 mr-2" /> Balance Sheet
          </Button>
          <Button onClick={() => { setReportType('audit-log'); setReportData(null); }} className="bg-orange-600 hover:bg-orange-700 justify-start">
            <FileText className="w-4 h-4 mr-2" /> Audit Log
          </Button>
        </div>
      </Card>
    </div>
  );
}
